import { Button, Card, DatePicker, Divider, Form, Grid, Input, Message, Select, Space } from "@arco-design/web-react";
import { InputCurrency, InputNumber } from "@/components/Numeric";
import { EnumSelect } from "@/components/Enum";
import { TermsDef } from "@/enums/common";
import { ReferenceTypeDef } from "@/enums/purchasing";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CompanyAPI } from "@/apis/system";
import { POAPI, VendorAPI } from "@/apis/purchasing";
import { CustomerAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import VendorSelect from "@/components/VendorSelect";
import Address from "@/components/Address";
import _ from "lodash";
import { Link } from "react-router-dom";
import Decimal from "decimal.js";
import { today } from "@/utils";

const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;
const TextArea = Input.TextArea;

export const POAdd = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [shipAddresses, setShipAddresses] = useState([]);
    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        const fetchItems = async () => {
            let values = await ItemAPI.actives();
            values = _.sortBy(values, "itemName");
            setItems(values);
        };
        fetchItems();
    }, []);

    useEffect(() => {
        const fetchCustomers = async () => {
            const values = await CustomerAPI.actives();
            let result = _.sortBy(values, "customerName");
            setCustomers(result);
        };
        fetchCustomers();
    }, []);

    useEffect(() => {
        const initForm = async () => {
            const companyShipToList = await CompanyAPI.addresses();
            const sorted = _.sortBy(companyShipToList, "contactName");
            setShipAddresses(sorted);

            let po = {
                poDate: today(),
                items: [{}]
            };

            if (_.size(sorted) > 0) {
                const addr = sorted[0];
                po.shipToId = addr.id;
                po.shipToContactName = addr.contactName;
                po.shipToAddress = addr.address;
                po.shipToCity = addr.city;
                po.shipToState = addr.state;
                po.shipToZip = addr.zip;
            }

            form.setFieldsValue(po);
        };
        initForm();
    }, [form]);

    const afterVendorSelected = async (vendorId) => {
        const vendorInfo = await VendorAPI.get(vendorId);
        form.setFieldValue('term', vendorInfo.term);
    }

    const getAddresses = useCallback(async (dropShip) => {
        let addresses = [];
        if (dropShip) {
            const customer = await CustomerAPI.get(dropShip);
            addresses = _.concat(addresses, customer.shipAddresses);
        } else {
            const companyShipToList = await CompanyAPI.addresses();
            addresses = _.concat(addresses, companyShipToList);
        }

        addresses = _.sortBy(addresses, "contactName");
        setShipAddresses(addresses);
        if (_.size(addresses) > 0) {
            const addr = addresses[0];
            form.setFieldValue("shipToId", addr.id);
            form.setFieldValue("shipToContactName", addr.contactName);
            form.setFieldValue("shipToAddress", addr.address);
            form.setFieldValue("shipToCity", addr.city);
            form.setFieldValue("shipToState", addr.state);
            form.setFieldValue("shipToZip", addr.zip);
        }
    }, [form]);

    const calcAmount = (index) => {
        const field = "items[" + index + "]";
        const quantity = form.getFieldValue(field + ".quantity");
        const rate = form.getFieldValue(field + ".rate");
        if (quantity && rate) {
            const amount = Decimal.mul(quantity, rate).toFixed(5, Decimal.ROUND_HALF_UP);
            form.setFieldValue(field + ".amount", amount);
        }
        calcTotalAmount();
    }

    const calcTotalAmount = () => {
        const formItems = form.getFieldValue("items");
        let total = new Decimal(0);
        for (let item of formItems) {
            total = Decimal.add(total, item.amount ?? 0).toNumber();
        }
        form.setFieldValue("totalAmount", total);
    }

    const onSubmit = async () => {
        try {
            const values = await form.validate();
            setLoading(true);
            const id = await POAPI.add(values);
            Message.success("Purchase Order created successfully");
            navigate(`/workspace/purchasing/po/${id}`);
        } catch (error) {
            if (error.name !== 'ValidationError') {
                Message.error(error.message || "Failed to create Purchase Order");
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Card title="Create New PO" extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Submit</Button>
                <Button onClick={() => navigate("/workspace/purchasing/po")}>Cancel</Button>
            </Space>
        }>
            <Form form={form} colon
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}>
                <FormItem field="id" hidden>
                    <Input />
                </FormItem>
                <FormItem>
                    <Row gutter={8}>
                        <Col span={8}>
                            <FormItem label="Vendor" field="vendorId"
                                labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
                                required rules={[{ required: true }]}>
                                <VendorSelect onChange={val => afterVendorSelected(val)} />
                            </FormItem>
                            <FormItem label="Drop Ship" field="dropShip" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                <Select options={_.map(customers, (item) => ({ value: item.id, label: item.customerName }))} allowClear
                                    showSearch
                                    onChange={val => {
                                        getAddresses(val);
                                    }} />
                            </FormItem>
                            <FormItem label="Ship To" field="shipToContactName" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
                                required rules={[{ required: true }]}>
                                <Address.Select
                                    dropdownRender={(menu) => (
                                        <div>
                                            {menu}
                                            <Divider style={{ marginTop: 0, marginBottom: 10 }} />
                                            <Link to={`/workspace/company/profile`} style={{ marginLeft: 20 }}>Add New</Link>
                                        </div>
                                    )}
                                    addresses={shipAddresses} onAddrChange={val => {
                                        form.setFieldValue("shipToAddress", val.address);
                                        form.setFieldValue("shipToCity", val.city);
                                        form.setFieldValue("shipToState", val.state);
                                        form.setFieldValue("shipToZip", val.zip);
                                    }} />
                            </FormItem>
                            <FormItem field="shipToAddress" hidden>
                                <Input />
                            </FormItem>
                            <FormItem field="shipToCity" hidden>
                                <Input />
                            </FormItem>
                            <FormItem field="shipToState" hidden>
                                <Input />
                            </FormItem>
                            <FormItem field="shipToZip" hidden>
                                <Input />
                            </FormItem>
                            <FormItem shouldUpdate noStyle>
                                {(value) => {
                                    return (
                                        <FormItem wrapperCol={{ offset: 8 }}>
                                            <Address value={{
                                                name: value.shipToContactName,
                                                address: value.shipToAddress,
                                                city: value.shipToCity,
                                                state: value.shipToState,
                                                zip: value.shipToZip,
                                            }} />
                                        </FormItem>
                                    )
                                }}
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="PO Date" field="poDate" labelCol={{ span: 9 }} required rules={[{ required: true }]}>
                                <DatePicker />
                            </FormItem>
                            <FormItem label="Term" field="term" labelCol={{ span: 9 }}>
                                <EnumSelect enumObj={TermsDef} />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="ETA" field="eta" labelCol={{ span: 9 }} required rules={[{ required: true }]}>
                                <DatePicker />
                            </FormItem>
                            <FormItem label="Due Date" field="dueDate" labelCol={{ span: 9 }} required rules={[{ required: true }]}>
                                <DatePicker />
                            </FormItem>
                            <FormItem label="Total Amount" field="totalAmount" labelCol={{ span: 9 }}>
                                <InputCurrency readOnly />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <FormItem label="Reference" labelCol={{ span: 2 }}>
                                <Form.List field="reference">
                                    {(fields, { add, remove }) =>
                                        <>
                                            {fields.map((item, index) => {
                                                return (
                                                    <FormItem key={item.key} style={{ marginBottom: 0 }}>
                                                        <Space className="form-item-row">
                                                            <FormItem field={item.field + ".id"} hidden>
                                                                <Input />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".type"}>
                                                                <EnumSelect enumObj={ReferenceTypeDef} order="label" style={{ width: 200 }} showSearch />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".number"}>
                                                                <Input />
                                                            </FormItem>
                                                            <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)}></Button>
                                                        </Space>
                                                    </FormItem>
                                                )
                                            })}
                                            <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Reference</Button>
                                        </>
                                    }
                                </Form.List>
                            </FormItem>
                            <FormItem label="Memo" labelCol={{ span: 2 }} field="memo">
                                <TextArea />
                            </FormItem>
                        </Col>
                    </Row>
                </FormItem>
                <FormItem label="Items" labelCol={{ span: 2 }}>
                    <Form.List field="items">
                        {(fields, { add, remove }) =>
                            <>
                                <table width="100%">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th width="120">Item Number</th>
                                            <th>Item Name</th>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>U/M</th>
                                            <th>Rate</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fields.map((item, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <span>#{index + 1}</span>
                                                        <FormItem field={item.field + ".id"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".itemNo"} required noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".itemId"} required rules={[{ required: true }]} noStyle>
                                                            <Select showSearch options={_.map(items, (it) => ({ value: it.id, label: it.itemName }))} style={{ minWidth: 120 }}
                                                                onChange={async (val) => {
                                                                    const vendorId = form.getFieldValue("vendorId");
                                                                    const poItem = await POAPI.getVendorLastItem(vendorId, val);
                                                                    const itemObj = _.find(items, it => it.id === val);
                                                                    form.setFieldValue(`${item.field}.itemNo`, itemObj.itemNo);
                                                                    form.setFieldValue(`${item.field}.itemName`, itemObj.itemName);
                                                                    form.setFieldValue(`${item.field}.description`, itemObj.description);
                                                                    form.setFieldValue(`${item.field}.uom`, itemObj.uom);
                                                                    form.setFieldValue(`${item.field}.rate`, poItem ? poItem.rate : itemObj.stdCost);
                                                                }} />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".itemName"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".description"} noStyle>
                                                            <Input />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".quantity"} required rules={[{ required: true }]} noStyle>
                                                            <InputNumber onChange={() => {
                                                                calcAmount(index);
                                                            }} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".uom"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".rate"} required rules={[{ required: true }]} noStyle>
                                                            <InputNumber onChange={() => {
                                                                calcAmount(index);
                                                            }} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".amount"} required rules={[{ required: true }]} noStyle>
                                                            <InputNumber readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem noStyle>
                                                            <Button type="text" status="danger" icon={<IconDelete />}
                                                                onClick={() => {
                                                                    remove(index);
                                                                    calcTotalAmount();
                                                                }}></Button>
                                                        </FormItem>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                                <FormItem>
                                    <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Item</Button>
                                </FormItem>
                            </>
                        }
                    </Form.List>
                </FormItem>
            </Form>
        </Card>
    );
}
